import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    IoClose, IoHeart, IoHeartOutline, IoShieldCheckmark, IoPencil, IoTrash,
    IoEllipsisVertical, IoShareSocialOutline, IoCheckmark, IoSend, IoWarningOutline, IoLocationSharp
} from 'react-icons/io5';
import { RiAlarmWarningLine } from 'react-icons/ri';
import {
    DetailModalOverlay, DetailModalContent, DetailHeader, DetailBody, DetailFooter,
    TypeBadge, LikeButton, AuthButton, CommentSection, CommentList, CommentItem,
    CommentInputBox, SpotThumbnail
} from '../styles/SpotStyles';
import SPOT_MESSAGES from '../constants/SpotMessages';
import { useAppContext } from '../../../contexts/AppContext';
import ReportModal from '../../../components/common/ReportModal';
import { getLocalizedTitle, getLocalizedContent } from '../utils/spotUtils'; // 🎯 [Import]

// ✅ [Fix] Helper for relative time - i18n 사용하도록 수정 (컴포넌트 내부로 이동 예정)

// 🎯 [UX 개선] 단일 이미지 슬라이더 - 한 번에 하나의 이미지만 크게 표시
// - 쿠팡/당근마켓 스타일의 상세뷰 이미지 갤러리
// - 스와이프로 탐색, 하단 점 인디케이터
const SingleImageSlider = ({ photos, onImageClick }) => {
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const sliderRef = React.useRef(null);

    const handleScroll = () => {
        if (sliderRef.current) {
            const scrollLeft = sliderRef.current.scrollLeft;
            const width = sliderRef.current.offsetWidth;
            const newIndex = Math.round(scrollLeft / width);
            if (newIndex !== currentIndex && newIndex >= 0 && newIndex < photos.length) {
                setCurrentIndex(newIndex);
            }
        }
    };

    return (
        <div style={{ marginBottom: '16px' }}>
            {/* 이미지 슬라이더 */}
            <div
                ref={sliderRef}
                onScroll={handleScroll}
                style={{
                    display: 'flex',
                    overflowX: 'auto',
                    scrollSnapType: 'x mandatory',
                    scrollBehavior: 'smooth',
                    WebkitOverflowScrolling: 'touch',
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                }}
            >
                <style>{`
                    .single-image-slider::-webkit-scrollbar { display: none; }
                `}</style>
                {photos.map((photo, index) => (
                    <div
                        key={index}
                        className="single-image-slider"
                        style={{
                            flexShrink: 0,
                            width: '100%',
                            minHeight: '250px',
                            maxHeight: '60vh',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            scrollSnapAlign: 'start',
                            background: '#F3F4F6',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            cursor: 'zoom-in',
                        }}
                        onClick={() => onImageClick(photo)}
                    >
                        <img
                            src={photo}
                            alt={`spot-${index + 1}`}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '60vh',
                                width: 'auto',
                                height: 'auto',
                                objectFit: 'contain',
                            }}
                        />
                    </div>
                ))}
            </div>

            {/* 점 인디케이터 (이미지가 2개 이상일 때만 표시) */}
            {photos.length > 1 && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '6px',
                    marginTop: '10px',
                }}>
                    {photos.map((_, index) => (
                        <div
                            key={index}
                            style={{
                                width: index === currentIndex ? '20px' : '8px',
                                height: '8px',
                                borderRadius: '4px',
                                background: index === currentIndex ? '#8B5CF6' : '#D1D5DB',
                                transition: 'all 0.3s ease',
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const SpotDetailModal = ({
    detailedSpot,
    piUser,
    t,
    onClose,
    onLike,
    onVerify,
    onEdit,
    onDelete,
    comments,
    // Comment Actions
    newComment,
    setNewComment,
    onAddComment,
    isAddingComment,
    // Comment Edit Actions
    editingCommentId,
    setEditingCommentId,
    editingCommentText,
    setEditingCommentText,
    onStartEditComment,
    onSaveEditComment,
    onCancelEditComment,
    onDeleteComment,
    setSelectedImage,
    // 🎯 [Refactor] Modal drag now self-managed (DealDetailModal pattern)
}) => {
    const { showAlert, landmarkSpotRate } = useAppContext();
    const { i18n, t: tModal } = useTranslation();  // ✅ [Fix] t 함수 추가 (getRelativeTime에서 사용)
    const currentLang = i18n.language;
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false); // 🎯 [NEW] 공유 팝업 모달
    const [showReportModal, setShowReportModal] = useState(false); // 🎯 [Moderation] 신고 모달

    // ✅ [Fix] Helper for relative time - i18n 사용
    const getRelativeTime = (dateParams) => {
        if (!dateParams) return '';
        const date = dateParams.toDate ? dateParams.toDate() : new Date(dateParams);
        const now = new Date();
        const diff = now - date;
        const seconds = Math.floor(diff / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (seconds < 60) return tModal('time_just_now') || 'just now';
        if (minutes < 60) return tModal('time_minutes_ago', { count: minutes }) || `${minutes}m`;
        if (hours < 24) return tModal('time_hours_ago', { count: hours }) || `${hours}h`;
        if (days < 7) return tModal('time_days_ago', { count: days }) || `${days}d`;
        return date.toLocaleDateString();
    };

    // 🎯 [Refactor] Self-managed drag state (DealDetailModal pattern)
    const [sheetY, setSheetY] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const startYRef = useRef(0);

    // 🎯 [Drag Handlers] Following DealDetailModal pattern
    const handleTouchStart = (e) => {
        startYRef.current = e.touches[0].clientY;
        setIsAnimating(false);
    };

    const handleTouchMove = (e) => {
        const currentY = e.touches[0].clientY;
        const diff = currentY - startYRef.current;
        if (diff > 0) {
            setSheetY(diff); // 1:1 Movement
        }
    };

    const handleTouchEnd = (e) => {
        const diff = e.changedTouches[0].clientY - startYRef.current;
        setIsAnimating(true);

        if (diff > 120) { // Close threshold
            onClose();
        } else {
            setSheetY(0); // Snap back
        }
    };

    if (!detailedSpot) return null;

    return (
        <DetailModalOverlay
            onClick={onClose}
            style={{ alignItems: 'flex-end' }}
        >
            <DetailModalContent
                onClick={(e) => e.stopPropagation()}
                style={{
                    transform: `translateY(${sheetY}px)`,
                    transition: isAnimating ? 'transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1)' : 'none',
                    height: '88vh',
                    maxHeight: '90vh',
                    borderTopLeftRadius: '20px',
                    borderTopRightRadius: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
                className="detail-modal-content"
            >
                {/* Drag Handle - 🎯 [Fix] Expanded touch area */}
                <div
                    className="drag-handle"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    style={{
                        width: '100%',
                        height: '24px',  // 🎯 Touch area: 24px (기존 4px → 확장)
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        cursor: 'grab',
                        touchAction: 'none'  // 🎯 브라우저 기본 스크롤 방지
                    }}
                >
                    {/* Visual Bar */}
                    <div style={{
                        width: '40px',
                        height: '4px',
                        background: '#D1D5DB',
                        borderRadius: '2px'
                    }} />
                </div>

                <DetailHeader>
                    {/* Row 1: Badge + Close Button */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <TypeBadge $type={detailedSpot.type}>
                            {detailedSpot.type === 'store' && <><IoShieldCheckmark /> STORE</>}
                            {detailedSpot.type === 'footprint' && <><IoLocationSharp /> POST</>}
                        </TypeBadge>
                        <button
                            onClick={onClose}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                        >
                            <IoClose size={22} color="#9CA3AF" />
                        </button>
                    </div>

                    {/* Row 2: Title */}
                    <h2 style={{ fontSize: '1.15rem', fontWeight: '700', margin: '0 0 8px 0', color: '#1F2937' }}>
                        {getLocalizedTitle(detailedSpot, currentLang)}
                    </h2>

                    {/* Row 3: Distance + Actions */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {detailedSpot.distance} • {detailedSpot.geo ? `${(detailedSpot.geo.lat || detailedSpot.geo.latitude)?.toFixed(4)}, ${(detailedSpot.geo.lng || detailedSpot.geo.longitude)?.toFixed(4)}` : ''}
                    </div>

                    {/* 🎯 [v4.1] 랜드마크 상세 정보 (점유자/본인 전용) - 예상 수익 & 남은 시간 */}
                    {detailedSpot.ownerId && (
                        <div style={{
                            marginTop: '4px',
                            padding: '8px 12px',
                            background: '#F3F4F6',
                            borderRadius: '8px',
                            fontSize: '0.85rem',
                            color: '#4B5563',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>👑 <strong>{detailedSpot.ownerName}</strong></span>
                                {detailedSpot.expiresAt && (
                                    <span style={{ color: '#EF4444', fontWeight: '600' }}>
                                        {getRelativeTime(detailedSpot.expiresAt)} 만료
                                    </span>
                                )}
                            </div>
                            {/* 본인인 경우 예상 수익 표시 */}
                            {piUser && (detailedSpot.ownerId === piUser.uid) && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6C5CE7', fontWeight: '600' }}>
                                    <span>💰 예상 수익 (24h Max)</span>
                                    {/* 실시간 계산 로직은 별도 컴포넌트나 훅 필요하나, 여기서는 단순 표시 */}
                                    <span>
                                        {/* DB에서 읽은 적립률(landmarkSpotRate) 사용 - DB 값 필수 */}
                                        {(() => {
                                            if (!detailedSpot.capturedAt) return '0 SPOT';
                                            if (!landmarkSpotRate) return 'Loading...';
                                            const captured = detailedSpot.capturedAt.toDate ? detailedSpot.capturedAt.toDate() : new Date(detailedSpot.capturedAt);
                                            const diffMin = Math.floor((new Date() - captured) / 60000);
                                            const cappedMin = Math.min(diffMin, 1440);
                                            return `+${(cappedMin * landmarkSpotRate).toFixed(1)} SPOT`;
                                        })()}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center', marginTop: '8px' }}>
                        {/* 🎯 [UI] 순서 변경: 인증 → 좋아요 */}

                        {/* Verify (Store Only) - verified 상태 색상만 표시 (배경 없음) */}
                        {detailedSpot.type === 'store' && (
                            <AuthButton
                                onClick={() => onVerify(detailedSpot)}
                                style={{
                                    padding: '6px 8px',
                                    fontSize: '0.8rem',
                                    width: 'auto',
                                    minWidth: '48px',
                                    ...(detailedSpot.userVerified && {
                                        color: '#6C5CE7'
                                    })
                                }}
                            >
                                <IoShieldCheckmark />
                                <span style={{ marginLeft: '2px', fontWeight: '600' }}>
                                    {detailedSpot.verificationCount > 999 ? '999+' : (detailedSpot.verificationCount || 0)}
                                </span>
                            </AuthButton>
                        )}

                        {/* Like - liked 상태 색상만 표시 (배경 없음) */}
                        <LikeButton
                            onClick={() => onLike(detailedSpot.id)}
                            style={{
                                padding: '6px 8px',
                                fontSize: '0.8rem',
                                width: 'auto',
                                minWidth: '48px',
                                ...(detailedSpot.liked && {
                                    color: '#EF4444'
                                })
                            }}
                        >
                            {detailedSpot.liked ? <IoHeart /> : <IoHeartOutline />}
                            <span style={{ marginLeft: '2px', fontWeight: '600' }}>
                                {detailedSpot.likeCount > 999 ? '999+' : (detailedSpot.likeCount || 0)}
                            </span>
                        </LikeButton>

                        {/* Edit (Owner) - 🎯 [UI 통일] Gray 스타일 */}
                        {piUser && (detailedSpot.userId === piUser.uid || detailedSpot.userName === piUser.username) && (
                            <button
                                onClick={onEdit}
                                style={{
                                    padding: '6px',
                                    background: '#F9FAFB',
                                    color: '#6B7280',
                                    border: '1px solid #E5E7EB',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '32px',
                                    minWidth: '32px'
                                }}
                            >
                                <IoPencil size={16} />
                            </button>
                        )}

                        {/* Delete (Owner) - 🎯 [UI 통일] Gray 스타일 */}
                        {piUser && (detailedSpot.userId === piUser.uid || detailedSpot.userName === piUser.username) && (
                            <button
                                onClick={onDelete}
                                style={{
                                    padding: '6px',
                                    background: '#F9FAFB',
                                    color: '#6B7280',
                                    border: '1px solid #E5E7EB',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '32px',
                                    minWidth: '32px'
                                }}
                            >
                                <IoTrash size={16} />
                            </button>
                        )}

                        {/* More Menu */}
                        <div style={{ position: 'relative' }}>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowMoreMenu(!showMoreMenu);
                                }}
                                style={{
                                    padding: '6px',
                                    background: '#F3F4F6',
                                    color: '#4B5563',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '32px',
                                    minWidth: '32px'
                                }}
                            >
                                <IoEllipsisVertical size={16} />
                            </button>

                            {showMoreMenu && (
                                <>
                                    <div
                                        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10 }}
                                        onClick={() => setShowMoreMenu(false)}
                                    />
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        right: 0,
                                        marginTop: '8px',
                                        background: 'white',
                                        borderRadius: '12px',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        zIndex: 20,
                                        minWidth: '120px',
                                        overflow: 'hidden',
                                        border: '1px solid #E5E7EB'
                                    }}>
                                        {/* Share - 🎯 [NEW] 공유 팝업 모달 열기 */}
                                        <button
                                            onClick={() => {
                                                setShowMoreMenu(false);
                                                setShowShareModal(true);
                                            }}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '8px',
                                                width: '100%', padding: '12px 16px',
                                                background: 'white', border: 'none',
                                                fontSize: '0.9rem', color: '#374151',
                                                cursor: 'pointer', borderBottom: '1px solid #F3F4F6',
                                                textAlign: 'left'
                                            }}
                                        >
                                            <IoShareSocialOutline size={16} /> {t('share')}
                                        </button>

                                        {/* Report - 원복: 준비 중 알림 */}
                                        {/* Report - 🎯 [Moderation] 신고 버튼 활성화 */}
                                        {piUser && detailedSpot.userId !== piUser?.uid && (
                                            <button
                                                onClick={() => {
                                                    setShowMoreMenu(false);
                                                    setShowReportModal(true);
                                                }}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: '8px',
                                                    width: '100%', padding: '12px 16px',
                                                    background: 'white', border: 'none',
                                                    fontSize: '0.9rem', color: '#EF4444',
                                                    cursor: 'pointer',
                                                    textAlign: 'left'
                                                }}
                                            >
                                                <RiAlarmWarningLine size={16} /> {t('report_action', 'Report')}
                                            </button>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                </DetailHeader>

                <DetailBody className="scrollable-content" style={{ flex: 1, overflowY: 'auto' }}>
                    {/* Images - 🎯 [UX] 단일 이미지 슬라이더로 변경 */}
                    {detailedSpot.photos?.length > 0 && (
                        <SingleImageSlider
                            photos={detailedSpot.photos}
                            onImageClick={(photo) => setSelectedImage(photo)}
                        />
                    )}

                    {/* Content */}
                    <div style={{ background: '#F9FAFB', padding: '16px', borderRadius: '12px', marginBottom: '20px' }}>
                        <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: '1.6', color: '#374151', whiteSpace: 'pre-wrap' }}>
                            {getLocalizedContent(detailedSpot, currentLang)}
                        </p>
                    </div>

                    {/* Comments */}
                    <CommentSection>
                        <h4 style={{ fontSize: '0.9rem', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>
                            {t(SPOT_MESSAGES.COMMENT_COUNT, { count: comments.length })}
                        </h4>
                        <CommentList>
                            {comments.map(comment => (
                                <CommentItem key={comment.id}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                        <strong>{comment.userName}</strong>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ fontSize: '0.7rem', color: '#9CA3AF' }}>{getRelativeTime(comment.createdAt)}</span>
                                            {/* My Comment Actions */}
                                            {piUser && (comment.userId === piUser.uid || comment.userName === piUser.username) && editingCommentId !== comment.id && (
                                                <>
                                                    <button
                                                        onClick={() => onStartEditComment(comment)}
                                                        style={{ background: 'none', border: 'none', color: '#6C5CE7', fontSize: '0.8rem', cursor: 'pointer', padding: '4px' }}
                                                    >
                                                        <IoPencil size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => onDeleteComment(comment.id)}
                                                        style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: '0.8rem', cursor: 'pointer', padding: '4px' }}
                                                    >
                                                        <IoTrash size={14} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    {/* Inline Edit */}
                                    {editingCommentId === comment.id ? (
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            <input
                                                type="text"
                                                value={editingCommentText}
                                                onChange={(e) => setEditingCommentText(e.target.value)}
                                                onTouchStart={(e) => e.stopPropagation()}
                                                onClick={(e) => e.stopPropagation()}
                                                style={{ flex: 1, padding: '6px 10px', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '0.85rem' }}
                                            />
                                            <button onClick={onSaveEditComment} style={{ padding: '6px 10px', background: '#6C5CE7', color: 'white', border: 'none', borderRadius: '6px', fontSize: '0.9rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <IoCheckmark size={16} />
                                            </button>
                                            <button onClick={onCancelEditComment} style={{ padding: '6px 10px', background: '#E5E7EB', color: '#374151', border: 'none', borderRadius: '6px', fontSize: '0.9rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <IoClose size={16} />
                                            </button>
                                        </div>
                                    ) : (
                                        <span>{comment.text}</span>
                                    )}
                                </CommentItem>
                            ))}
                            {comments.length === 0 && (
                                <p style={{ color: '#9CA3AF', fontSize: '0.85rem', textAlign: 'center', padding: '16px 0' }}>
                                    {t(SPOT_MESSAGES.COMMENT_FIRST)}
                                </p>
                            )}
                        </CommentList>
                    </CommentSection>
                </DetailBody>

                <DetailFooter style={{
                    paddingBottom: 'max(20px, calc(env(safe-area-inset-bottom) + 60px))' // 🎯 [Fix] Avoid bottom nav overlap
                }}>
                    <CommentInputBox style={{ flex: 1 }}>
                        <input
                            type="text"
                            placeholder={t(SPOT_MESSAGES.COMMENT_PLACEHOLDER)}
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && !isAddingComment && onAddComment()}
                            onTouchStart={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                            style={{ fontSize: '16px' }}
                            disabled={isAddingComment}
                        />
                        <button onClick={onAddComment} disabled={isAddingComment} style={{ opacity: isAddingComment ? 0.5 : 1 }}>
                            {isAddingComment ? '...' : <IoSend size={16} />}
                        </button>
                    </CommentInputBox>
                </DetailFooter>
            </DetailModalContent>

            {/* 🎯 [NEW] 공유 팝업 모달 */}
            {
                showShareModal && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0, left: 0, right: 0, bottom: 0,
                            background: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1100,
                            padding: '20px'
                        }}
                        onClick={() => setShowShareModal(false)}
                    >
                        <div
                            style={{
                                background: 'white',
                                borderRadius: '16px',
                                padding: '24px',
                                width: '100%',
                                maxWidth: '400px',
                                boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 style={{
                                margin: '0 0 16px 0',
                                fontSize: '1.1rem',
                                fontWeight: '700',
                                color: '#374151'
                            }}>
                                {t('share')}
                            </h3>

                            {/* URL 표시 - 🎯 [Deep Link] spotId 포함 */}
                            <div style={{
                                background: '#F3F4F6',
                                borderRadius: '8px',
                                padding: '12px',
                                marginBottom: '16px',
                                wordBreak: 'break-all',
                                fontSize: '0.85rem',
                                color: '#6B7280',
                                border: '1px solid #E5E7EB'
                            }}>
                                {`${window.location.origin}${window.location.pathname}#/main?spotId=${detailedSpot.id}`}
                            </div>

                            {/* 복사 버튼 */}
                            <button
                                onClick={async () => {
                                    // 🎯 [Deep Link] spotId 포함 URL 생성
                                    const shareUrl = `${window.location.origin}${window.location.pathname}#/main?spotId=${detailedSpot.id}`;
                                    try {
                                        await navigator.clipboard.writeText(shareUrl);
                                        showAlert(t(SPOT_MESSAGES.LINK_COPIED));
                                    } catch (err) {
                                        // Legacy fallback
                                        const textArea = document.createElement('textarea');
                                        textArea.value = shareUrl;
                                        textArea.style.position = 'fixed';
                                        textArea.style.opacity = '0';
                                        document.body.appendChild(textArea);
                                        textArea.select();
                                        document.execCommand('copy');
                                        document.body.removeChild(textArea);
                                        showAlert(t(SPOT_MESSAGES.LINK_COPIED));
                                    }
                                    setShowShareModal(false);
                                }}
                                style={{
                                    width: '100%',
                                    padding: '14px',
                                    background: '#6C5CE7',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '12px',
                                    fontSize: '0.95rem',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                }}
                            >
                                <IoCheckmark size={18} /> {t('copy_url')}
                            </button>

                            {/* 닫기 버튼 */}
                            <button
                                onClick={() => setShowShareModal(false)}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    background: 'transparent',
                                    color: '#6B7280',
                                    border: 'none',
                                    fontSize: '0.9rem',
                                    marginTop: '8px',
                                    cursor: 'pointer'
                                }}
                            >
                                {t('close')}
                            </button>
                        </div>
                    </div>
                )
            }
            {/* 🎯 [Moderation] 신고 모달 */}
            <ReportModal
                isOpen={showReportModal}
                onClose={() => setShowReportModal(false)}
                spotId={detailedSpot.id}
            />
        </DetailModalOverlay >
    );
};

export default SpotDetailModal;
